/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Eye, AlertTriangle, UserX, Users, Shield, Camera, RefreshCw } from 'lucide-react';
import Page from '../../../components/Page';
import adminServices from '../../../services/adminServices';

const VIOLATION_LABELS = {
  TAB_SWITCH: { label: 'Tab Switch', color: 'bg-orange-100 text-orange-700' },
  WINDOW_BLUR: { label: 'Window Blur', color: 'bg-yellow-100 text-yellow-700' },
  NO_FACE: { label: 'No Face', color: 'bg-red-100 text-red-700' },
  MULTIPLE_FACES: { label: 'Multiple Faces', color: 'bg-red-100 text-red-700' },
};

export default function ProctoringMonitor() {
  const [assessmentId, setAssessmentId] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const intervalRef = useRef(null);

  async function fetchViolations(id) {
    const targetId = id || assessmentId || 'all';
    setLoading(true);
    const res = await adminServices.getExamViolations(targetId);
    if (res.ok) setStudents(res.data || []);
    setLoading(false);
  }

  useEffect(() => { fetchViolations('all'); }, []);

  function startPolling() {
    if (!assessmentId) return;
    fetchViolations();
    setPolling(true);
    intervalRef.current = setInterval(() => fetchViolations(), 4000);
  }

  function stopPolling() {
    setPolling(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }

  useEffect(() => { return () => { if (intervalRef.current) clearInterval(intervalRef.current); }; }, []);

  const totalStudents = students.length;
  const flagged = students.filter(s => s.totalViolations >= 2 && s.totalViolations < 3).length;
  const autoSubmitted = students.filter(s => s.totalViolations >= 3).length;

  return (
    <Page title="Proctoring Monitor">
      <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
        <div className="mb-5">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Shield size={22} className="text-purple-600" /> Exam Proctoring Monitor
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Monitor student violations during active exams</p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-5 flex items-center gap-3 border border-gray-100">
          <input type="text" value={assessmentId} onChange={e => setAssessmentId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchViolations()}
            placeholder="Enter Assessment ID..."
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
          <button onClick={() => fetchViolations()} disabled={!assessmentId || loading}
            className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50">
            {loading ? 'Loading...' : 'Search'}
          </button>
          {!polling ? (
            <button onClick={startPolling} disabled={!assessmentId}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50">
              Auto-Refresh
            </button>
          ) : (
            <button onClick={stopPolling}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 flex items-center gap-1">
              <RefreshCw size={12} className="animate-spin" /> Stop
            </button>
          )}
        </div>

        {/* Summary */}
        {students.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
            <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <div className="bg-blue-50 rounded-lg p-2.5"><Users size={18} className="text-blue-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{totalStudents}</p><p className="text-xs text-gray-500">Students</p></div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <div className="bg-green-50 rounded-lg p-2.5"><Eye size={18} className="text-green-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{totalStudents - flagged - autoSubmitted}</p><p className="text-xs text-gray-500">Warning (1)</p></div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <div className="bg-orange-50 rounded-lg p-2.5"><AlertTriangle size={18} className="text-orange-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{flagged}</p><p className="text-xs text-gray-500">Flagged (2)</p></div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <div className="bg-red-50 rounded-lg p-2.5"><UserX size={18} className="text-red-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{autoSubmitted}</p><p className="text-xs text-gray-500">Auto-Submitted (3)</p></div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {students.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Camera size={32} className="mx-auto mb-2 opacity-40" />
              <p>{assessmentId ? 'No violations found for this assessment.' : 'Enter an Assessment ID and click Search.'}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-12 px-5 py-3 bg-gray-50 text-xs font-semibold text-gray-400 uppercase">
                <div className="col-span-3">Student</div>
                <div className="col-span-2">Violations</div>
                <div className="col-span-3">Last Type</div>
                <div className="col-span-2">Last Time</div>
                <div className="col-span-2">Status</div>
              </div>
              {students.map((s) => {
                const count = s.totalViolations || 0;
                const type = s.lastViolationType || '-';
                const cfg = VIOLATION_LABELS[type] || { label: type, color: 'bg-gray-100 text-gray-600' };
                const isAutoSub = count >= 3;
                const isFlagged = count >= 2 && !isAutoSub;

                return (
                  <div key={s.userId} className={`grid grid-cols-12 px-5 py-3 border-t border-gray-50 items-center ${isAutoSub ? 'bg-red-50/50' : isFlagged ? 'bg-orange-50/50' : 'hover:bg-gray-50'}`}>
                    <div className="col-span-3">
                      <p className="text-sm font-medium text-gray-800">{s.firstName} {s.lastName}</p>
                      <p className="text-xs text-gray-400">ID: {s.userId}</p>
                    </div>
                    <div className="col-span-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${count === 1 ? 'bg-yellow-100 text-yellow-700' : count === 2 ? 'bg-orange-100 text-orange-700' : count >= 3 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {count}
                      </span>
                    </div>
                    <div className="col-span-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                    </div>
                    <div className="col-span-2 text-xs text-gray-400">
                      {s.lastOccurredAt ? new Date(s.lastOccurredAt).toLocaleTimeString() : '-'}
                    </div>
                    <div className="col-span-2">
                      {isAutoSub ? <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">Auto-Submitted</span>
                        : isFlagged ? <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">Flagged</span>
                        : <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Active</span>
                      }
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </Page>
  );
}
